/**
 * AdminDashboard — the staff command center. Shows every project grouped by the
 * month it was submitted (newest first), bucketed by what action it needs:
 *   - Needs review  (SUBMITTED)         → open the estimate editor to review/send
 *   - Awaiting acceptance (SENT)        → customer has the quote; nudge/follow up
 *   - Follow up on payment (ACCEPTED, unpaid) → chase the deposit/payment
 *   - Won (ACCEPTED + paid)
 * Drafts (OPEN) are shown muted at the bottom of their month.
 *
 * A pipeline summary across all months sits at the top. Staff-only.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminDashboard, getMe, type DashboardStaffProject } from '../../lib/api';

function dollars(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

/**
 * Row title: the project's name, else the customer's name, else a short id so
 * every row is still distinguishable (unnamed rows are test/legacy data).
 */
function projectTitle(p: DashboardStaffProject): string {
  if (p.projectName) return p.customerName ? `${p.projectName} \u00b7 ${p.customerName}` : p.projectName;
  if (p.customerName) return p.customerName;
  return `Project ${p.projectId.slice(0, 8)}`;
}

/** Optional subline: budget + timeline, only when present (omitted entirely if neither). */
function subline(p: DashboardStaffProject): string {
  return [p.budgetLabel ? `Budget: ${p.budgetLabel}` : null, p.timelineLabel]
    .filter(Boolean)
    .join(' \u00b7 ');
}

type Bucket = 'review' | 'sent' | 'payment' | 'won' | 'draft';

function bucketOf(p: DashboardStaffProject): Bucket {
  switch (p.status) {
    case 'SUBMITTED':
      return 'review';
    case 'SENT':
      return 'sent';
    case 'ACCEPTED':
      return p.paid ? 'won' : 'payment';
    default:
      return 'draft'; // OPEN / CANCELLED
  }
}

const BUCKET_LABEL: Record<Bucket, string> = {
  review: 'Needs review',
  sent: 'Awaiting acceptance',
  payment: 'Follow up on payment',
  won: 'Won',
  draft: 'Drafts',
};

// Order buckets by urgency within a month.
const BUCKET_ORDER: Bucket[] = ['review', 'sent', 'payment', 'won', 'draft'];

/** The date a project is grouped/sorted by: submitted, else created. */
function groupDate(p: DashboardStaffProject): string {
  return p.submittedAt ?? p.createdAt;
}

/** "September 2026" from an ISO date. */
function monthKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function monthLabel(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export function AdminDashboard(): JSX.Element {
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [projects, setProjects] = useState<DashboardStaffProject[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getMe()
      .then((me) => {
        const ok = me.role === 'ADMINISTRATOR' || me.role === 'ESTIMATOR';
        setAllowed(ok);
        if (ok) return getAdminDashboard().then(setProjects);
        return undefined;
      })
      .catch(() => {
        setAllowed(false);
        setError('We couldn\u2019t load the dashboard.');
      });
  }, []);

  // Pipeline totals across every month.
  const pipeline = useMemo(() => {
    const counts: Record<Bucket, number> = { review: 0, sent: 0, payment: 0, won: 0, draft: 0 };
    for (const p of projects ?? []) counts[bucketOf(p)] += 1;
    return counts;
  }, [projects]);

  // Group by month (desc), then bucket within each month.
  const months = useMemo(() => {
    const byMonth = new Map<string, { label: string; items: DashboardStaffProject[] }>();
    for (const p of projects ?? []) {
      const iso = groupDate(p);
      const key = monthKey(iso);
      if (!byMonth.has(key)) byMonth.set(key, { label: monthLabel(iso), items: [] });
      byMonth.get(key)!.items.push(p);
    }
    return [...byMonth.entries()]
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([key, { label, items }]) => {
        const buckets = BUCKET_ORDER.map((b) => ({
          bucket: b,
          items: items.filter((p) => bucketOf(p) === b),
        })).filter((g) => g.items.length > 0);
        return { key, label, buckets };
      });
  }, [projects]);

  function openProject(p: DashboardStaffProject): void {
    // Needs-review → estimate editor; everything else → bids/project detail.
    if (bucketOf(p) === 'review') navigate(`/admin/estimate/${p.projectId}`);
    else navigate(`/admin/bids/${p.projectId}`);
  }

  if (allowed === null) {
    return <div className="flow flow--single"><div className="stage-card"><p className="stage-card__lede">Checking access&hellip;</p></div></div>;
  }
  if (!allowed) {
    return (
      <div className="flow flow--single"><div className="stage-card">
        <h1>Not available</h1>
        <p className="stage-card__lede">This area is for staff only.</p>
      </div></div>
    );
  }

  return (
    <div className="flow flow--single">
      <div className="stage-card">
        <h1>Dashboard</h1>
        <p className="stage-card__lede">Projects by month, grouped by what needs your attention.</p>

        {error ? <div className="pb-note pb-note--flag" role="alert">{error}</div> : null}

        {/* Pipeline summary */}
        <div className="dash-summary">
          {(['review', 'sent', 'payment', 'won'] as Bucket[]).map((b) => (
            <div key={b} className={`dash-stat dash-stat--${b}`}>
              <span className="dash-stat__count">{pipeline[b]}</span>
              <span className="dash-stat__label">{BUCKET_LABEL[b]}</span>
            </div>
          ))}
        </div>

        {!projects ? (
          <p className="stage-card__lede">Loading&hellip;</p>
        ) : projects.length === 0 ? (
          <p className="stage-card__lede">No projects yet.</p>
        ) : (
          months.map((m) => (
            <div key={m.key} className="dash-month">
              <div className="dash-month__head">{m.label}</div>
              {m.buckets.map((g) => (
                <div key={g.bucket} className="dash-bucket">
                  <div className={`dash-bucket__head dash-bucket__head--${g.bucket}`}>
                    {BUCKET_LABEL[g.bucket]} <span className="dash-bucket__count">{g.items.length}</span>
                  </div>
                  {g.items.map((p) => (
                    <button
                      key={p.projectId}
                      type="button"
                      className="pb-option"
                      style={{ width: '100%', textAlign: 'left', marginBottom: 'var(--pb-1)', opacity: g.bucket === 'draft' ? 0.6 : 1 }}
                      onClick={() => openProject(p)}
                    >
                      <span style={{ flexGrow: 1 }}>
                        <span className="pb-option__label">{projectTitle(p)}</span>
                        {subline(p) ? <span className="pb-option__sub">{subline(p)}</span> : null}
                      </span>
                      <span style={{ textAlign: 'right', minWidth: '5rem' }}>
                        <span className="pb-option__label" style={{ color: 'var(--pb-chalk)' }}>
                          est. {dollars(p.totalCents)}
                        </span>
                        <span className="pb-option__sub">{p.status}</span>
                      </span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
